import React, {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { useVirtualizer } from "@tanstack/react-virtual";
import classes from "./ClientsList.module.css";
import useTheme from "../../../../common/hooks/useTheme";
import { platformActions } from "../../store/platform-slice";
import {
  deleteInvitedCostumer,
  resendInvitationToCostumer,
} from "../../../../common/network/Api";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ClientType } from "../../../../common/entities/profile";
import { useLocation } from "react-router-dom";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";
import { appendChatModeToPath } from "../../../chat/chatMode";
import ScrollableWrapper from "../common/ScrollableWrapper";
import MoreIcon from "../../svg/MoreIcon";
import NutritionIcon from "../../svg/NutritionIcon";
import WorkoutIcon from "../../svg/WorkoutIcon";
import ReportIcon from "../../svg/ReportIcon";
import { formatDisplayDate } from "../../../../common/utils/date";
import { Theme } from "../../../../providers/ThemeProvider";
import CustomTooltip from "../common/CustomTooltip";
import { Backdrop, Snackbar } from "@mui/material";
import { HowToReg } from "@mui/icons-material";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import { SlideTransition } from "../../ScreenWrappers/PlatformSnackbars";
import { openClientTab } from "../../utils/clientTabs";
import ServiceIcon from "../../svg/ServiceIcon";
import MoreOptionsPopover from "../common/MoreOptionsPopover";
import ReviewHealthScoresModal from "./ReviewHealthScoresModal";
import ScoreApprovalBadge from "./ScoreApprovalBadge";
import LabSvg from "../../../../common/svgs/LabSvg";

type Props = {
  clientFilter: string;
  setIsLoadingCustomerInfo: Function;
  activeTab: number;
  selectedSources: string[];
  selectedScores: string[];
  onClientsUpdated?: (clients: ClientType[]) => void;
};

export type ClientsListRef = {
  fetchClients: () => Promise<void>;
  getClients: () => ClientType[];
  /** Scrolls the list to the first client under `letter`, or to the nearest
   *  letter that has clients (forward first, then back). */
  scrollToLetter: (letter: string) => void;
};

// One rendered line of the list: the client plus whether it opens its letter
// group. Rows are a fixed 46px with the wrapper's 8px gap between them, so
// the virtualizer can place them without measuring.
type ClientRow = {
  client: ClientType & { firstName: string };
  letter: string;
  showLetter: boolean;
};
const ROW_HEIGHT = 46;
const ROW_GAP = 8;
const ROW_PITCH = ROW_HEIGHT + ROW_GAP;
// Bottom breathing space (replaces .tableBody padding-bottom).
const LIST_PADDING_END = 80;
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const statusMessages = {
  REGISTERED:
    "This client has completed registration and is active.(All features are available to them.)",
  PENDING:
    "This client hasn’t accepted the invitation yet.(You may resend the invite or wait for them to respond.)",
  BOUNCE:
    "This email could not be delivered. The address might be invalid or unreachable.(A bounce means the recipient’s server rejected the message.)",
  SPAM: "The recipient marked this invite as spam.(Further attempts may not be delivered.)",
  DEFERRED:
    "The message was accepted by the server but hasn’t been delivered yet.(It will retry automatically — no action needed.)",
};

const StatusBadge = ({ status }: { status: string }) => {
  const theme: Theme = useTheme();

  const normalizedStatus =
    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().trim();

  const tooltipMessage =
    statusMessages[status as keyof typeof statusMessages] || normalizedStatus;

  return (
    <div style={{ display: "flex", justifyContent: "flex-start" }}>
      <CustomTooltip message={tooltipMessage} customHeight="auto">
        <div
          className={classes.badgeContainer}
          style={{
            borderColor:
              status === "REGISTERED"
                ? theme.colorTokens.S[70]
                : status === "PENDING"
                ? theme.colorTokens.W[70]
                : theme.colorTokens.D[70],
            backgroundColor:
              status === "REGISTERED"
                ? theme.colorTokens.S[10]
                : status === "PENDING"
                ? theme.colorTokens.W[10]
                : theme.colorTokens.D[10],
          }}
        >
          {normalizedStatus}
        </div>
      </CustomTooltip>
    </div>
  );
};

const ClientsList = forwardRef<ClientsListRef, Props>(
  (
    {
      clientFilter,
      setIsLoadingCustomerInfo,
      activeTab,
      selectedSources,
      selectedScores,
      onClientsUpdated,
    },
    ref
  ) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const location = useLocation();
    const {
      ASSetOpenSnackBar,
      ASSetSnackBarMessage,
      ASSetTypeSnackBar,
      // ASSetSelectedClient / ASSetIsLoadingPlatformData are gone with the
      // in-place client navigation: clicking a client now opens its own tab
      // (openClientTab) and that tab's WorkspaceShell owns the client context.
      ASLoadClients,
    } = useAppStore();
    const { AuthUser } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [clients, setClients] = useState<ClientType[]>([]);
    const [snackBarContent, setSnackBarContent] = useState({
      title: "",
      message: "",
      action: () => {},
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewClient, setReviewClient] = useState<ClientType | null>(null);

    const handleDeleteClient = async (clientEmail: string) => {
      setIsDeleting(true);
      try {
        await deleteInvitedCostumer(clientEmail);

        // Refresh the clients list (force past the shared cache)
        await fetchClients(true);

        // Show success message
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: "",
          message: "Client invitation has been successfully deleted.",
        });
        ASSetTypeSnackBar("success");
      } catch (err) {
        // Show error message
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: "Failed to delete client invitation.",
          message:
            "We couldn't delete the client invitation. Please try again.",
        });
        ASSetTypeSnackBar("alert");
      } finally {
        setIsDeleting(false);
        // Close the snackbar
        setSnackBarContent({
          title: "",
          message: "",
          action: () => {},
        });
      }
    };

    const handleResendInvitation = async (clientEmail: string) => {
      setIsResending(true);
      try {
        await resendInvitationToCostumer(clientEmail);

        // Show success message
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: "",
          message: "Invitation has been successfully resent.",
        });
        ASSetTypeSnackBar("success");
      } catch (err) {
        console.log("test", err);
        // Show error message
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: "Failed to resend invitation.",
          message: "We couldn't resend the invitation. Please try again.",
        });
        ASSetTypeSnackBar("alert");
      } finally {
        setIsResending(false);
        // Close the snackbar
        setSnackBarContent({
          title: "",
          message: "",
          action: () => {},
        });
      }
    };

    const handleReviewHealthScore = (client: ClientType) => {
      // Clear stale platform data so the modal fetches fresh data for this client
      dispatch(platformActions.clearDataPlatformServer());
      setReviewClient(client);
      setReviewModalOpen(true);
    };

    const handleReviewSaveSuccess = () => {
      fetchClients(true);
    };

    const listContainerRef = useRef<HTMLDivElement>(null);

    // Sources the list from the shared AppStore loader (de-dupes with Breadcrumb).
    // A failed/inconsistent load rejects → we flag an error so the UI shows a
    // Retry state instead of a misleading "No clients found". Pass force to bypass
    // the shared cache after a mutation (delete/resend/invite/review).
    const fetchClients = useCallback(
      async (force = false) => {
        try {
          setIsLoading(true);
          setHasError(false);
          const list = await ASLoadClients(force ? { force: true } : undefined);
          setClients(list);
          onClientsUpdated?.(list);
        } catch (error) {
          console.error("Error fetching clients:", error);
          setHasError(true);
        } finally {
          setIsLoading(false);
        }
      },
      [onClientsUpdated, ASLoadClients]
    );

    // Expose fetchClients and getClients functions to parent component
    useImperativeHandle(ref, () => ({
      fetchClients: () => fetchClients(true),
      getClients: () => clients,
      scrollToLetter: (letter: string) => {
        const present = new Set(rows.filter((r) => r.showLetter).map((r) => r.letter));
        const start = ALPHABET.indexOf(letter);
        let target: string | undefined;
        for (let i = start; i < ALPHABET.length && !target; i++) {
          if (present.has(ALPHABET[i])) target = ALPHABET[i];
        }
        for (let i = start - 1; i >= 0 && !target; i--) {
          if (present.has(ALPHABET[i])) target = ALPHABET[i];
        }
        if (!target) return;
        const index = rows.findIndex((r) => r.showLetter && r.letter === target);
        // An instant jump: a smooth scroll across thousands of rows would
        // stream the whole alphabet past, and it never runs in a hidden tab.
        if (index >= 0) virtualizer.scrollToIndex(index, { align: "start" });
      },
    }));

    // Fetch a client list for the affiliates
    useEffect(() => {
      fetchClients();
    }, [fetchClients]);

    useEffect(() => {
      if (listContainerRef.current) {
        listContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, [activeTab, selectedSources, selectedScores]);

    // Filter clients based on the search text
    const filteredClients =
      clients &&
      clients
        .filter((client) => {
          const fullName =
            `${client.firstName} ${client.lastName}`.toLowerCase();
          return (
            fullName.includes(clientFilter.toLowerCase()) ||
            client.email.toLowerCase().includes(clientFilter.toLowerCase())
          );
        })
        .filter((client) => {
          if (activeTab === 0) {
            return true; // All clients
          } else if (activeTab === 1) {
            return client.status === "REGISTERED"; // Registered clients
          } else if (activeTab === 2) {
            return client.status !== "REGISTERED"; // Non-registered clients
          }
          return false; // Default case should not happen
        })
        .filter((client) => {
          if (selectedSources.length === 0) {
            return true; // No filter applied, show all clients
          }
          if (client.origin === "" && selectedSources.includes("PNOE")) {
            return true; // Include PNOE clients if selected
          }
          return selectedSources.includes(client.origin);
        })
        .filter((client) => {
          if (selectedScores.length === 0) {
            return true; // No filter applied, show all clients
          }
          // Only filter clients that have a score badge
          const hasScore =
            client.pending === 0 &&
            client.status === "REGISTERED" &&
            (client.report || client.biomarker);
          if (!hasScore) {
            return false;
          }
          const approvalMap: Record<number, string> = {
            0: "pending",
            1: "pending",
            2: "hidden",
            3: "partially_approved",
            4: "approved",
          };
          const scoreKey = approvalMap[client.approval ?? -1];
          return scoreKey !== undefined && selectedScores.includes(scoreKey);
        })
        .sort((a, b) => {
          // Sort alphabetically by firstName (with trimStart to handle leading spaces)
          const nameA = (a.firstName || "").trimStart().toLowerCase();
          const nameB = (b.firstName || "").trimStart().toLowerCase();
          return nameA.localeCompare(nameB);
        });

    // The lines to draw, in list order, each knowing whether it opens its
    // letter group. Recomputed only when the filtered list changes.
    const rows = useMemo<ClientRow[]>(() => {
      const seen = new Set<string>();
      const result: ClientRow[] = [];
      (filteredClients || []).forEach((client) => {
        // Provide default firstName if missing
        const firstName =
          client.firstName?.trimStart() || client.email?.split("@")[0] || "Unknown";
        if (firstName.length === 0) return;
        const letter = firstName.charAt(0).toUpperCase();
        const showLetter = letter !== " " && !seen.has(letter);
        if (showLetter) seen.add(letter);
        result.push({ client: { ...client, firstName }, letter, showLetter });
      });
      return result;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clients, clientFilter, activeTab, selectedSources, selectedScores]);

    // Only the rows in view (plus a few either side) exist in the DOM: with
    // thousands of clients, painting every row froze each keystroke for
    // seconds. The wrapper stays the scroll container.
    const virtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => listContainerRef.current,
      estimateSize: () => ROW_PITCH,
      overscan: 8,
      paddingEnd: LIST_PADDING_END,
    });

    // Function to render the first letter of the client's name
    const renderClientRowGroupLetter = (
      letter: string,
      shouldDisplayLetter: boolean
    ) => {
      if (shouldDisplayLetter) {
        return (
          <div
            className={classes.tableBodyRowLetter}
            style={{
              borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
            }}
          >
            {letter}
          </div>
        );
      }
      return null;
    };

    // Render a single client row
    const renderClientRow = (
      client: any,
      index: number,
      shouldDisplayLetter: boolean
    ) => {
      const firstLetter = client.firstName.trimStart().charAt(0).toUpperCase();

      const onClientSelected = (client: any) => {
        if (client.status !== "REGISTERED") {
          return;
        }
        if (client.pending === 1) {
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "No Biometric Data Available",
            message: `The customer with email ${
              client ? client.email : ""
            }, has not completed the biometric questionnaire yet. Please inform them to complete it.`,
          });
          ASSetTypeSnackBar("alert");
          return;
        }

        // Open the client's workspace in its own browser tab (one client per
        // tab; re-clicking focuses the existing tab). Must stay synchronous —
        // an await before window.open trips popup blockers.
        const didOpen = openClientTab(
          client.id,
          appendChatModeToPath(
            `/client/dashboard?id=${client.id}`,
            location.search
          )
        );

        // A blocked popup returns null from window.open and would otherwise
        // leave the row looking simply unresponsive. Deliberately no same-tab
        // fallback: loading the client here would replace the client list and
        // contradict the one-client-per-tab model.
        if (!didOpen) {
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Pop-up blocked",
            message:
              "Your browser blocked the new tab. Allow pop-ups for this site to open clients in their own tab.",
          });
          ASSetTypeSnackBar("alert");
        }
      };

      return (
        <div key={index} className={classes.tableBodyRowContainer}>
          <div className={classes.tableBodyRowLetterContainer}>
            {renderClientRowGroupLetter(firstLetter, shouldDisplayLetter)}
          </div>
          {/* The click lives on the card only: the letter column to its left
              is a group label, not part of the client. */}
          <div
            className={classes.tableBodyRow}
            onClick={() => onClientSelected(client)}
            style={{
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
              borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              backgroundColor:
                client.status !== "REGISTERED"
                  ? theme.colorTokens.N[20]
                  : client.pending === 1
                  ? theme.colorTokens.N[20]
                  : theme.colors_ds.NEUTRAL_BG_MAIN,
              cursor: client.status !== "REGISTERED" ? "default" : "pointer",
            }}
          >
            <div className={classes.tableBodyRowItem} style={{ flex: "1" }}>
              {client.firstName}
            </div>
            <div className={classes.tableBodyRowItem} style={{ flex: "1" }}>
              {client.lastName}
            </div>
            {activeTab === 2 && (
              <div className={classes.tableBodyRowItem} style={{ flex: "2" }}>
                {client.email}
              </div>
            )}
            {(activeTab === 0 || activeTab === 2) && (
              <div
                className={classes.tableBodyRowItem}
                style={{
                  flex: "1",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                }}
              >
                <StatusBadge status={client.status} />
              </div>
            )}
            {(activeTab === 0 || activeTab === 1) && (
              <div className={classes.tableBodyRowItem} style={{ flex: "1.3" }}>
                {client.pending === 0 &&
                  client.status === "REGISTERED" &&
                  (client.report || client.biomarker) && (
                    <ScoreApprovalBadge approval={client.approval} />
                  )}
              </div>
            )}
            {activeTab === 2 && (
              <div className={classes.tableBodyRowItem} style={{ flex: "1" }}>
                {formatDisplayDate(client.dateCreated)}
              </div>
            )}
            {(activeTab === 0 || activeTab === 1) && (
              <div className={classes.tableBodyRowItem3} style={{ flex: "1" }}>
                {client.pending === 0 && (
                  <CustomTooltip message={"Onboarding"} customHeight="auto">
                    <HowToReg
                      sx={{
                        fontSize: "20px",
                        color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
                      }}
                    />
                  </CustomTooltip>
                )}
                {client.report && (
                  <CustomTooltip message={"Report"} customHeight="auto">
                    <ReportIcon />
                  </CustomTooltip>
                )}
                {client.biomarker && (
                  <CustomTooltip message={"Biomarkers"} customHeight="auto">
                    <LabSvg
                      fill={theme.colorTokens.N[90]}
                      width={20}
                      height={18}
                    />
                  </CustomTooltip>
                )}
                {client.nutrition && (
                  <CustomTooltip message={"Nutrition"} customHeight="auto">
                    <NutritionIcon />
                  </CustomTooltip>
                )}
                {client.workout && (
                  <CustomTooltip message={"Workout"} customHeight="auto">
                    <WorkoutIcon />
                  </CustomTooltip>
                )}
                {client.prescription && (
                  <CustomTooltip
                    message={"Prescription services"}
                    customHeight="auto"
                  >
                    <ServiceIcon />
                  </CustomTooltip>
                )}
              </div>
            )}
            <div className={classes.tableBodyRowItem} style={{ flex: "0.6" }}>
              <div
                className={classes.importedBadge}
                style={{
                  backgroundColor: client.isImport
                    ? theme.colorTokens.P[10]
                    : theme.colorTokens.N[30],
                  borderColor: client.isImport
                    ? theme.colorTokens.P[90]
                    : theme.colorTokens.N[80],
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                }}
              >
                {client.isImport ? client.origin : "PNOE"}
              </div>
            </div>
            <div
              className={`${classes.tableBodyRowItem} ${classes.tableBodyRowItem4}`}
              style={{ flex: "0.25", overflow: "visible" }}
              onClick={(e) => e.stopPropagation()}
            >
              {client.status === "REGISTERED" && client.approval !== 0 ? (
                <MoreOptionsPopover
                  icon={
                    <MoreIcon fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT} />
                  }
                  options={[
                    {
                      label:
                        client.approval === 1
                          ? "Review health score"
                          : "Edit health score",
                      onClick: () => handleReviewHealthScore(client),
                    },
                  ]}
                  minWidth="180px"
                />
              ) : client.status !== "REGISTERED" &&
                (activeTab === 0 || activeTab === 2) ? (
                <MoreOptionsPopover
                  icon={
                    <MoreIcon fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT} />
                  }
                  options={[
                    {
                      label: "Resend",
                      onClick: () => {
                        setSnackBarContent({
                          title: "Resend this invitation?",
                          message:
                            "This will resend the original email to the client.",
                          action: () => handleResendInvitation(client.email),
                        });
                      },
                    },
                    {
                      label: "Delete",
                      onClick: () => {
                        setSnackBarContent({
                          title:
                            "Are you sure you want to remove this invitation?",
                          message:
                            "Removing this invite will delete it from the pending list. The client will not be notified.",
                          action: () => handleDeleteClient(client.email),
                        });
                      },
                    },
                  ]}
                  minWidth="120px"
                />
              ) : null}
            </div>
          </div>
        </div>
      );
    };

    // Render all client rows
    const renderClientRows = () => {
      if (isLoading) {
        return (
          <Skeleton
            count={10}
            height={46}
            borderRadius={8}
            style={{ lineHeight: 0, paddingBottom: "16px" }}
          />
        );
      }

      if (hasError) {
        return (
          <div
            className={classes.noClients}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div>We couldn't load your clients. Please try again.</div>
            <PrimaryButton
              label="Retry"
              onClick={() => fetchClients(true)}
              sx={{ width: "160px" }}
            />
          </div>
        );
      }

      if (!clients || clients.length === 0) {
        return <div className={classes.noClients}>No clients found</div>;
      }

      if (filteredClients.length === 0) {
        return (
          <div className={classes.noClients}>
            No clients match your search or filter criteria.
          </div>
        );
      }

      return (
        // flexShrink 0: the wrapper is a flex column and would otherwise
        // squash this to its own height, leaving nothing to scroll.
        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: "relative",
            flexShrink: 0,
          }}
        >
          {virtualizer.getVirtualItems().map((item) => {
            const row = rows[item.index];
            return (
              <div
                key={row.client.id ?? item.index}
                // Positioned with `top`, not `transform: translateY()`: a
                // transform makes each row its own stacking context, so the
                // row's "…" menu (z-index 1000 inside it) paints under the
                // rows that follow.
                style={{
                  position: "absolute",
                  top: item.start,
                  left: 0,
                  width: "100%",
                }}
              >
                {renderClientRow(row.client, item.index, row.showLetter)}
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className={classes.container}>
        <div className={classes.table}>
          <div
            className={classes.tableHeader}
            style={{ color: theme.colors_ds.NEUTRAL_TEXT_WEAK }}
          >
            <div className={classes.tableHeaderItem} style={{ flex: "1" }}>
              First Name
            </div>
            <div className={classes.tableHeaderItem} style={{ flex: "1" }}>
              Last Name
            </div>
            {activeTab === 2 && (
              <div className={classes.tableHeaderItem} style={{ flex: "2" }}>
                Email Account
              </div>
            )}
            {(activeTab === 0 || activeTab === 2) && (
              <div className={classes.tableHeaderItem} style={{ flex: "1" }}>
                Registration status
              </div>
            )}
            {(activeTab === 0 || activeTab === 1) && (
              <div className={classes.tableHeaderItem} style={{ flex: "1.3" }}>
                Scores
              </div>
            )}
            {activeTab === 2 && (
              <div className={classes.tableHeaderItem} style={{ flex: "1" }}>
                Sent on
              </div>
            )}
            {(activeTab === 0 || activeTab === 1) && (
              <div className={classes.tableHeaderItem} style={{ flex: "1" }}>
                Services
              </div>
            )}
            <div className={classes.tableHeaderItem} style={{ flex: "0.6" }}>
              Sources
            </div>
            <div
              className={classes.tableHeaderItem}
              style={{ flex: "0.25", marginRight: "-20px" }}
            >
              {/*PLACEHOLDER*/}
            </div>
          </div>

          <ScrollableWrapper ref={listContainerRef}>
            {renderClientRows()}
          </ScrollableWrapper>
        </div>
        <Backdrop
          open={snackBarContent.title !== ""}
          style={{
            color: "#fff",
            zIndex: 2000, // Ensure it's above most components
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        />
        <Snackbar
          sx={{ zIndex: 2001 }}
          open={snackBarContent.title !== ""}
          message={
            <div
              style={{
                width: "450px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="60"
                height="60"
                viewBox="0 0 61 60"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M24.875 3.75H36.125L34.25 37.5H26.75L24.875 3.75ZM36.125 50.625C36.125 53.7316 33.6066 56.25 30.5 56.25C27.3934 56.25 24.875 53.7316 24.875 50.625C24.875 47.5184 27.3934 45 30.5 45C33.6066 45 36.125 47.5184 36.125 50.625Z"
                  fill="#3B3F42"
                />
              </svg>
              <p
                style={{
                  fontSize: "14px",
                  fontFamily: "var(--font-Bold)",
                  color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
                  textAlign: "center",
                  marginBottom: "0px",
                }}
              >
                {snackBarContent.title}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  fontFamily: "var(--font-Medium)",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                  textAlign: "center",
                }}
              >
                {snackBarContent.message}
              </p>
              <div style={{ display: "flex", gap: "16px", width: "100%" }}>
                <SecondaryButton
                  label={"Cancel"}
                  onClick={() => {
                    if (!isDeleting && !isResending) {
                      setSnackBarContent({
                        title: "",
                        message: "",
                        action: () => {},
                      });
                    }
                  }}
                  isDisable={isDeleting || isResending}
                  sx={{ width: "100%" }}
                />
                <PrimaryButton
                  label={
                    isDeleting
                      ? "Deleting..."
                      : isResending
                      ? "Sending..."
                      : "Confirm"
                  }
                  onClick={() => {
                    if (!isDeleting && !isResending) {
                      snackBarContent.action();
                    }
                  }}
                  isDisable={isDeleting || isResending}
                  sx={{ width: "100%" }}
                />
              </div>
            </div>
          }
          TransitionComponent={SlideTransition}
          key="slide-transition"
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          ContentProps={{
            style: {
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
            },
          }}
        />
        {reviewClient && (
          <ReviewHealthScoresModal
            open={reviewModalOpen}
            onClose={() => {
              setReviewModalOpen(false);
              setReviewClient(null);
            }}
            clientId={reviewClient.id}
            userRole={AuthUser.role}
            clientName={`${reviewClient.firstName} ${reviewClient.lastName}`}
            clientApproval={reviewClient.approval}
            clientGender={Number(reviewClient.gender)}
            onSaveSuccess={handleReviewSaveSuccess}
          />
        )}
      </div>
    );
  }
);

ClientsList.displayName = "ClientsList";

export default ClientsList;
